import React, { useEffect, useMemo, useState } from 'react';
import { CreditCard, X, Upload, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../api/client';
import GestisciTessereStaff from '../components/GestisciTessereStaff';
import ImportaCsvUtentiModal from '../components/ImportaCsvUtentiModal';
import { useAuth } from '../context/AuthContext';
import { puoFare } from '../utils/permessi';
import useIsMobile from '../utils/useIsMobile';

import { FEDERAZIONI, COLORI as FEDERAZIONE_COLORI, etichetta, scadenzaInfo, giorniAllaScadenza } from '../utils/tessere';

const chiave = (u) => u ? (u.user_id ? `u${u.user_id}` : `p${u.persona_id}`) : null;
const ultimaPerFederazione = (tessere, fed) => tessere.find(t => t.federazione === fed) || null;

// filtri (09/10/2026): tessera × stagione × tipo di persona, combinabili con la ricerca
const FILTRI_TESSERA = [['tutti', 'Tutti'], ['FIPAV', 'Con FIPAV'], ['ASI_A', 'Con ASI A'], ['ASI_B', 'Con ASI B'], ['entrambe', 'ASI + FIPAV'], ['nessuna', 'Senza tessera'], ['no_ASI_B', 'Senza ASI B']];
const TIPI = [['tutti', 'Tutti'], ['corsisti', 'Corsisti (nel planning)'], ['utenti', 'Utenti'], ['senza_account', 'Senza account'], ['allenatori', 'Allenatori'], ['staff', 'Staff']];
// corsista = in almeno un gruppo del planning nella stagione scelta (o in una qualsiasi), 14/10/2026
const corsistaIn = (u, stagione) => !!u.corsista_stagioni?.length && (!stagione || u.corsista_stagioni.includes(stagione));
const tipoOk = (u, tipo, stagione) => tipo === 'tutti'
  || (tipo === 'corsisti' && corsistaIn(u, stagione))
  || (tipo === 'utenti' && u.ruolo === 'UTENTE')
  || (tipo === 'senza_account' && u.senza_account)
  || (tipo === 'allenatori' && (u.ruolo === 'ALLENATORE' || u.is_allenatore))
  || (tipo === 'staff' && !u.senza_account && !['UTENTE', 'ALLENATORE'].includes(u.ruolo));
const tessereDi = (u, stagione) => u.tesseramenti.filter(t => !stagione || t.stagione === stagione);
// filtro scadenza (12/10/2026): guarda la tessera più recente di ogni federazione
const FILTRI_SCADENZA = [['', 'Qualsiasi scadenza'], ['scadute', 'Scadute'], ['30', 'Scadono entro 30 giorni'], ['90', 'Scadono entro 90 giorni'], ['con_data', 'Con scadenza indicata'], ['stagione', 'Valide tutta la stagione']];
const scadenzaOk = (u, filtro, stagione) => {
  if (!filtro) return true;
  const ultime = FEDERAZIONI.map(f => tessereDi(u, stagione).find(t => t.federazione === f)).filter(Boolean);
  return ultime.some(t => {
    const gg = giorniAllaScadenza(t);
    if (filtro === 'scadute') return gg !== null && gg < 0;
    if (filtro === 'con_data') return !!t.data_scadenza;
    if (filtro === 'stagione') return !t.data_scadenza;
    return gg !== null && gg >= 0 && gg <= +filtro;
  });
};
const tesseraOk = (u, filtro, stagione) => {
  const feds = new Set(tessereDi(u, stagione).map(t => t.federazione));
  if (filtro === 'tutti') return true;
  if (filtro === 'nessuna') return feds.size === 0;
  if (filtro === 'no_ASI_B') return !feds.has('ASI_B');
  if (filtro === 'entrambe') return (feds.has('ASI_A') || feds.has('ASI_B')) && feds.has('FIPAV');
  return feds.has(filtro);
};

// Export (23/10/2026, titolare): una riga per tessera delle persone filtrate (rispetta la stagione
// scelta), chi non ha tessere compare una volta con le colonne tessera vuote. Filtri Excel attivi.
const esportaExcel = (persone, stagione, descrizioneFiltri) => {
  const ruoloDi = (u) => u.senza_account ? 'Senza account' : u.ruolo === 'UTENTE' ? 'Utente' : u.ruolo === 'ALLENATORE' || u.is_allenatore ? 'Allenatore' : 'Staff';
  const righe = [];
  for (const u of persone) {
    const base = { 'Nome': u.nome, 'Codice fiscale': u.codice_fiscale || '', 'Email': u.email || '', 'Tipo': ruoloDi(u), 'Nel planning (stagioni)': (u.corsista_stagioni || []).join(', ') };
    const tessere = tessereDi(u, stagione);
    if (!tessere.length) righe.push({ ...base, 'Federazione': '', 'Numero tessera': '', 'Stagione': '', 'Scadenza': '' });
    for (const t of tessere) righe.push({ ...base, 'Federazione': etichetta(t.federazione), 'Numero tessera': t.numero_tessera || '', 'Stagione': t.stagione || '', 'Scadenza': t.data_scadenza ? new Date(t.data_scadenza).toLocaleDateString('it-IT') : 'fine stagione' });
  }
  const ws = XLSX.utils.json_to_sheet(righe);
  ws['!autofilter'] = { ref: ws['!ref'] };
  ws['!cols'] = [28, 19, 28, 14, 20, 12, 16, 11, 13].map(wch => ({ wch }));
  const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Tesseramenti');
  XLSX.writeFile(wb, `Tesseramenti_${new Date().toISOString().slice(0, 10)}.xlsx`);
  api.post('/tesseramenti/export-registro', { righe: righe.length, filtri: descrizioneFiltri }).catch(() => {});
};

export default function Tesseramenti() {
  const mobile = useIsMobile();
  const { user } = useAuth();
  const [importa, setImporta] = useState(false);
  const [quante, setQuante] = useState(50); // telefono: 50 alla volta (19/10/2026)
  const [utenti, setUtenti] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selezionato, setSelezionato] = useState(null);
  const [tessera, setTessera] = useState('tutti');
  const [stagione, setStagione] = useState('');
  const [tipo, setTipo] = useState('tutti');
  const [disattivati, setDisattivati] = useState(false);
  const [scadenza, setScadenza] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/tesseramenti').then(r => setUtenti(r.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const stagioni = useMemo(() => [...new Set(utenti.flatMap(u => [...u.tesseramenti.map(t => t.stagione), ...(u.corsista_stagioni || [])]))].sort().reverse(), [utenti]);
  // controllo corsisti (14/10/2026, titolare): chi è nel planning dev'essere tesserato ASI B per quella stagione
  const stagioneCorsi = useMemo(() => [...new Set(utenti.flatMap(u => u.corsista_stagioni || []))].sort().reverse()[0] || null, [utenti]);
  const corsistiSenzaB = stagioneCorsi ? utenti.filter(u => corsistaIn(u, stagioneCorsi) && !u.tesseramenti.some(t => t.federazione === 'ASI_B' && t.stagione === stagioneCorsi)) : [];
  const vediCorsistiSenzaB = () => { setTipo('corsisti'); setTessera('no_ASI_B'); setStagione(stagioneCorsi); setScadenza(''); setSearch(''); setDisattivati(true); };
  // base = tutti i filtri tranne quello "tessera", per i contatori sulle pillole
  const base = utenti.filter(u =>
    (disattivati || u.attivo !== false) && tipoOk(u, tipo, stagione) && scadenzaOk(u, scadenza, stagione) &&
    `${u.nome} ${u.email || ''} ${u.codice_fiscale || ''}`.toLowerCase().includes(search.toLowerCase())
  );
  const filtrati = base.filter(u => tesseraOk(u, tessera, stagione));
  const filtriAttivi = tessera !== 'tutti' || stagione || tipo !== 'tutti' || disattivati || search || scadenza;

  const pannello = selezionato && (
    <div style={s.panelSotto}>
      <div style={s.panelHeader}>
        <span style={s.panelTitle}>{selezionato.nome}</span>
        <button style={s.closeBtn} onClick={() => { setSelezionato(null); load(); }} aria-label="Chiudi"><X size={16}/></button>
      </div>
      <div style={s.panelBody}>
        <GestisciTessereStaff personaId={selezionato.persona_id} />
      </div>
    </div>
  );

  return (
    <div style={{ display:'flex', gap:'20px' }}>
      <div style={{ flex:1 }}>
        <div style={{ marginBottom:'20px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'10px', flexWrap:'wrap' }}>
            <div>
              <h1 style={s.title}>TESSERAMENTI</h1>
              <p style={s.sub}>{filtriAttivi ? `${filtrati.length} di ${utenti.length}` : utenti.length} utenti · codici ASI A/B e FIPAV collegati all'anagrafica</p>
            </div>
            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
              <button style={s.pill} disabled={!filtrati.length} onClick={() => esportaExcel(filtrati, stagione, [tessera !== 'tutti' && tessera, stagione, tipo !== 'tutti' && tipo, scadenza && `scadenza ${scadenza}`, search && `cerca "${search}"`, disattivati && 'anche disattivati'].filter(Boolean).join(', '))}>
                <Download size={13} style={{ verticalAlign:'-2px' }} /> ESPORTA EXCEL{filtriAttivi ? ` (${filtrati.length})` : ''}
              </button>
              {puoFare(user, 'tesseramenti', 'edit') && <button style={{ ...s.pill, ...s.pillOn }} onClick={() => setImporta(true)}><Upload size={13} style={{ verticalAlign:'-2px' }} /> IMPORTA TESSERE</button>}
            </div>
          </div>
        </div>
        {importa && <ImportaCsvUtentiModal soloTessere onClose={() => setImporta(false)} onDone={load} />}

        {stagioneCorsi && (
          <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'12px', padding:'10px 14px', borderRadius:'10px', border:`1px solid ${corsistiSenzaB.length ? '#F59E0B' : '#22C55E'}60`, background: corsistiSenzaB.length ? '#FEF3C7' : '#22C55E15', color: corsistiSenzaB.length ? '#92400E' : '#15803D', fontSize:'var(--font-size-sm)' }}>
            <span style={{ flex:1, minWidth:'200px' }}>
              {corsistiSenzaB.length
                ? <><b>⚠ {corsistiSenzaB.length} corsisti</b> nel planning senza tessera <b>ASI B {stagioneCorsi}</b></>
                : <>✓ Tutti i corsisti nel planning hanno la tessera ASI B {stagioneCorsi}</>}
            </span>
            {corsistiSenzaB.length > 0 && <button style={{ ...s.pill, ...s.pillOn }} onClick={vediCorsistiSenzaB}>MOSTRA</button>}
          </div>
        )}
        <input style={{ ...s.search, marginBottom:'12px' }} placeholder="Cerca per nome, email o codice fiscale..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={s.filtri}>
          <div style={s.gruppo}>
            {FILTRI_TESSERA.map(([v, l]) => (
              <button key={v} style={{ ...s.pill, ...(tessera === v ? s.pillOn : {}) }} onClick={() => setTessera(v)}>
                {l} <span style={s.conta}>{base.filter(u => tesseraOk(u, v, stagione)).length}</span>
              </button>
            ))}
          </div>
          <div style={s.gruppo}>
            <select style={s.select} value={stagione} onChange={e => setStagione(e.target.value)}>
              <option value="">Qualsiasi stagione</option>
              {stagioni.map(x => <option key={x} value={x}>Stagione {x}</option>)}
            </select>
            <select style={s.select} value={scadenza} onChange={e => setScadenza(e.target.value)}>
              {FILTRI_SCADENZA.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select style={s.select} value={tipo} onChange={e => setTipo(e.target.value)}>
              {TIPI.map(([v, l]) => <option key={v} value={v}>{v === 'tutti' ? 'Tutte le persone' : l}</option>)}
            </select>
            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'var(--font-size-xs)', color:'var(--text-muted)', cursor:'pointer' }}>
              <input type="checkbox" checked={disattivati} onChange={e => setDisattivati(e.target.checked)} /> anche disattivati
            </label>
            {filtriAttivi && <button style={s.azzera} onClick={() => { setTessera('tutti'); setStagione(''); setTipo('tutti'); setDisattivati(false); setSearch(''); setScadenza(''); }}>Azzera filtri</button>}
          </div>
        </div>

        {loading && <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-sm)' }}>Caricamento...</div>}

        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {(mobile ? filtrati.slice(0, quante) : filtrati).map(u => {
            const ultime = Object.fromEntries(FEDERAZIONI.map(f => [f, ultimaPerFederazione(tessereDi(u, stagione), f)]));
            return (
              <React.Fragment key={chiave(u)}>
              {mobile ? (
                // telefono: riga compatta — nome/email, tessere presenti come etichette, bottone
                <div style={{ ...s.riga, ...s.rigaMobile }} onClick={() => { if (chiave(selezionato) === chiave(u)) { setSelezionato(null); load(); } else setSelezionato(u); }}>
                  <div style={{ flex:'1 1 100%', minWidth:0 }}>
                    <div style={{ fontWeight:'700', fontSize:'15px' }}>{u.nome}{corsistaIn(u, stagione) && <span title={`Nel planning corsi: ${u.corsista_stagioni.join(', ')}`} style={{ marginLeft:'8px', fontSize:'10px', fontWeight:700, color:'#C24B08', border:'1px solid #F4600C50', borderRadius:'4px', padding:'1px 5px', verticalAlign:'middle' }}>CORSISTA</span>}</div>
                    <div style={{ color:'var(--text-dim)', fontSize:'12px', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.senza_account ? `senza account · CF ${u.codice_fiscale || '—'}` : `${u.email} · ${u.ruolo}`}</div>
                  </div>
                  <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center', flex:1, minWidth:0 }}>
                    {FEDERAZIONI.filter(fed => ultime[fed]).map(fed => (
                      <span key={fed} style={{ ...s.badge, background:`${FEDERAZIONE_COLORI[fed]}20`, color:FEDERAZIONE_COLORI[fed], borderColor:`${FEDERAZIONE_COLORI[fed]}40` }}>{etichetta(fed)} {ultime[fed].numero_tessera}{scadenzaInfo(ultime[fed]) && <span style={{ color: scadenzaInfo(ultime[fed]).colore, fontWeight:600 }}> · {scadenzaInfo(ultime[fed]).testo}</span>}</span>
                    ))}
                    {!FEDERAZIONI.some(fed => ultime[fed]) && <span style={{ color:'var(--text-dim)', fontSize:'12px' }}>Nessuna tessera</span>}
                  </div>
                  <span style={{ ...s.btnGestisci, pointerEvents:'none' }}><CreditCard size={13}/> {chiave(selezionato) === chiave(u) ? 'CHIUDI' : 'GESTISCI'}</span>
                </div>
              ) : (
              <div style={s.riga}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:'700', fontSize:'var(--font-size-sm)' }}>{u.nome}{u.attivo === false && <span style={{ color:'var(--text-dim)', fontWeight:600 }}> · disattivato</span>}{corsistaIn(u, stagione) && <span title={`Nel planning corsi: ${u.corsista_stagioni.join(', ')}`} style={{ marginLeft:'8px', fontSize:'10px', fontWeight:700, color:'#C24B08', border:'1px solid #F4600C50', borderRadius:'4px', padding:'1px 5px', verticalAlign:'middle' }}>CORSISTA</span>}</div>
                  <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)', marginTop:'2px' }}>{u.senza_account ? <>senza account · CF {u.codice_fiscale || '—'} <span title="Si aggancerà quando la persona si registra al portale con questo codice fiscale" style={{ color:'#C24B08', fontWeight:700 }}>· in attesa di registrazione</span></> : `${u.email} · ${u.ruolo}`}</div>
                </div>
                {FEDERAZIONI.map(fed => {
                  const t = ultime[fed];
                  return (
                    <div key={fed} style={{ width:'120px', flexShrink:0 }}>
                      {t ? (
                        <div>
                          <span style={{ ...s.badge, background:`${FEDERAZIONE_COLORI[fed]}20`, color:FEDERAZIONE_COLORI[fed], borderColor:`${FEDERAZIONE_COLORI[fed]}40` }}>{etichetta(fed)}</span>
                          <div style={{ fontFamily:'monospace', fontSize:'var(--font-size-xs)', marginTop:'4px' }}>{t.numero_tessera}</div>
                          <div style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)' }}>{t.stagione}</div>
                          {scadenzaInfo(t) && <div title={scadenzaInfo(t).titolo} style={{ color: scadenzaInfo(t).colore, fontSize:'var(--font-size-xs)', fontWeight: scadenzaInfo(t).colore === 'var(--text-dim)' ? 400 : 700 }}>{scadenzaInfo(t).testo}</div>}
                        </div>
                      ) : (
                        <span style={{ color:'var(--text-dim)', fontSize:'var(--font-size-xs)' }}>— {etichetta(fed)} —</span>
                      )}
                    </div>
                  );
                })}
                <button style={{ ...s.btnGestisci, ...(chiave(selezionato) === chiave(u) ? s.btnAperto : {}) }} onClick={() => { if (chiave(selezionato) === chiave(u)) { setSelezionato(null); load(); } else setSelezionato(u); }}>
                  <CreditCard size={13}/> {chiave(selezionato) === chiave(u) ? 'CHIUDI' : 'GESTISCI'}
                </button>
              </div>
              )}
              {/* il riquadro di gestione si apre subito sotto la persona (telefono e PC, richiesta titolare 09/10) */}
              {chiave(selezionato) === chiave(u) && pannello}
              </React.Fragment>
            );
          })}
          {mobile && filtrati.length > quante && (
            <button onClick={() => setQuante(q => q + 50)}
              style={{ width:'100%', padding:'12px', margin:'8px 0', borderRadius:'12px', border:'1px solid var(--border2)', background:'var(--bg-input)', color:'var(--text)', fontWeight:700, cursor:'pointer' }}>
              Mostra altre {Math.min(50, filtrati.length - quante)} (ne restano {filtrati.length - quante})
            </button>
          )}
          {!loading && filtrati.length === 0 && (
            <div style={{ textAlign:'center', color:'var(--text-dim)', padding:'40px' }}>Nessun utente con questi filtri</div>
          )}
        </div>
      </div>

    </div>
  );
}

const s = {
  filtri:     { display:'flex', flexDirection:'column', gap:'10px', marginBottom:'18px' },
  gruppo:     { display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center' },
  pill:       { border:'1px solid var(--border2)', background:'var(--bg-card)', color:'var(--text-muted)', borderRadius:'999px', padding:'6px 12px', fontSize:'13px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' },
  pillOn:     { background:'var(--text)', color:'var(--bg-card)', borderColor:'var(--text)' },
  conta:      { opacity:.65, marginLeft:'4px', fontWeight:700 },
  select:     { border:'1px solid var(--border2)', background:'var(--bg-card)', color:'var(--text)', borderRadius:'999px', padding:'6px 12px', fontSize:'13px' },
  azzera:     { border:'none', background:'transparent', color:'#C24B08', fontWeight:700, fontSize:'13px', cursor:'pointer', padding:'6px 4px' },
  title:      { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:        { color:'var(--text-dim)', fontSize:'var(--font-size-sm)', marginTop:'4px' },
  search:     { width:'100%', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'8px', padding:'11px 14px', color:'var(--text)', fontSize:'var(--font-size-sm)', outline:'none', marginBottom:'20px', boxSizing:'border-box' },
  riga:       { display:'flex', alignItems:'center', gap:'16px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'10px', padding:'14px 18px' },
  badge:      { padding:'2px 8px', borderRadius:'4px', border:'1px solid', fontSize:'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px' },
  btnGestisci:{ display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input)', border:'1px solid var(--border2)', borderRadius:'6px', padding:'7px 12px', color:'var(--text-muted)', fontSize:'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', flexShrink:0 },
  rigaMobile: { flexWrap:'wrap', gap:'8px 10px', padding:'10px 12px', cursor:'pointer' },
  panelSotto: { background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'14px', marginTop:'-2px', maxWidth:'560px' },
  btnAperto:  { background:'var(--text)', color:'var(--bg-card)', borderColor:'var(--text)' },
  panelHeader:{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid var(--border)' },
  panelTitle: { fontFamily:"'Barlow Condensed'", fontSize:'var(--font-size-sm)', fontWeight:'700', letterSpacing:'2px' },
  closeBtn:   { background:'transparent', border:'none', color:'var(--text-dim)', cursor:'pointer' },
  panelBody:  { padding:'20px' },
};
